import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OrganizationLink from './OrganizationLink';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../Utils/SnackBarContext';
import { apiRequest } from '../Utils/apiClient';
import { useUser } from './WebSocketContext';

const OrganizationSwitcher = ({ primaryColor = '#5757b0', onNavigate }) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const {
    organizationId,
    organizationRole,
    organizations,
    validationOnly,
    switchOrganization
  } = useUser();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [switching, setSwitching] = useState(false);
  const canManageOrganization = !validationOnly && ['owner', 'admin'].includes(
    String(organizationRole || '').toLowerCase()
  );

  useEffect(() => {
    if (!canManageOrganization) setDialogOpen(false);
  }, [canManageOrganization]);

  const availableOrganizations = useMemo(
    () => (Array.isArray(organizations) ? organizations : []).filter((organization) => organization?.id),
    [organizations]
  );

  if (!availableOrganizations.length || !organizationId) {
    return null;
  }

  const activeOrganization = availableOrganizations.find((organization) => String(organization.id) === String(organizationId));

  const handleSwitch = async (nextOrganizationId) => {
    setAnchorEl(null);
    if (switching) return;
    if (String(nextOrganizationId) === String(organizationId)) {
      return;
    }
    setSwitching(true);
    try {
      await switchOrganization(nextOrganizationId);
    } catch (error) {
      openSnackbar(error?.message || t('organization.switch_error'), 3500, 'error');
    } finally {
      setSwitching(false);
    }
  };

  const handleCreate = async () => {
    if (!canManageOrganization || !name.trim() || saving) {
      return;
    }
    setSaving(true);
    try {
      const response = await apiRequest('/api/v1/organizations', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), slug: slug.trim() || undefined })
      });
      const nextOrganizationId = response?.active_organization_id || response?.organization?.id;
      if (nextOrganizationId) {
        await switchOrganization(nextOrganizationId);
      }
      setDialogOpen(false);
      setName('');
      setSlug('');
      openSnackbar(t('organization.created'), 2500, 'success');
    } catch (error) {
      openSnackbar(error?.message || t('organization.create_error'), 3500, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        id="organization-switcher-button"
        fullWidth
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? 'organization-switcher-menu' : undefined}
        aria-label={`${t('organization.switch')}: ${activeOrganization?.name || t('organization.label')}`}
        aria-busy={switching}
        disabled={switching}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={switching ? <CircularProgress size={18} color="inherit" /> : <KeyboardArrowDownIcon />}
        sx={{ justifyContent: 'flex-start', minWidth: 0, textAlign: 'left', textTransform: 'none', color: '#fff',
          borderRadius: '8px', py: 0.75, px: 0, minHeight: 40,
          '& .MuiButton-endIcon': { ml: 0.5, mr: 0, flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 18 } },
          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          '&.Mui-disabled': { color: 'rgba(255,255,255,0.8)' },
          '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: 2 } }}
      >
        <Box component="span" sx={{ minWidth: 0 }}>
          <Typography component="span" variant="body2" noWrap title={activeOrganization?.name}
            sx={{ display: 'block', fontWeight: 600, fontSize: '0.8125rem' }}>
            {activeOrganization?.name || t('organization.label')}
          </Typography>
        </Box>
      </Button>
      <Menu
        id="organization-switcher-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': 'organization-switcher-button' }}
        PaperProps={{ sx: { width: anchorEl?.clientWidth, minWidth: 240, maxWidth: 'calc(100vw - 24px)',
          maxHeight: '60vh', borderRadius: 2, mt: 0.5, '& .MuiMenuItem-root': { minHeight: 44, whiteSpace: 'normal' } } }}
      >
        {availableOrganizations.map((organization) => (
          <MenuItem key={organization.id} selected={String(organization.id) === String(organizationId)}
            onClick={() => handleSwitch(organization.id)}>
            <ListItemText sx={{ overflowWrap: 'anywhere' }}>{organization.name}</ListItemText>
            {String(organization.id) === String(organizationId) && <CheckIcon sx={{ ml: 1, color: primaryColor }} fontSize="small" />}
          </MenuItem>
        ))}
        {canManageOrganization && <Divider />}
        {canManageOrganization && (
          <MenuItem component={OrganizationLink} onClick={() => { setAnchorEl(null); onNavigate?.(); }}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('organization.manage')}</ListItemText>
          </MenuItem>
        )}
        {canManageOrganization && (
          <MenuItem onClick={() => { setAnchorEl(null); setDialogOpen(true); }}>
            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('organization.create')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={dialogOpen && canManageOrganization} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('organization.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            autoFocus
            label={t('organization.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
          />
          <TextField
            label={t('organization.slug')}
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || saving}
            variant="contained"
            sx={{ backgroundColor: primaryColor }}
          >
            {t('organization.create_submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationSwitcher;
